/** Public Community Rankings — the platform-wide, privacy-safe Deej XP board. */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useServices } from '../services/context.js';
import { UserAvatar } from '../components/UserAvatar.js';
import { formatTimeAgo } from '../components/relativeTime.js';
import { useNow } from '../hooks/useNow.js';
import type { PlatformLeaderboardEntry, PlatformLeaderboardPage, PlatformLeaderboardView } from '../services/types.js';

const PAGE_SIZE = 50;
const VIEWS: Array<{ id: PlatformLeaderboardView; label: string; title: string; copy: string; metric: (r: PlatformLeaderboardEntry) => number; rank: (r: PlatformLeaderboardEntry) => number }> = [
  { id: 'overall', label: 'Deej XP', title: 'Deej XP', copy: 'Lifetime progress across badges, playing, making and competing.', metric: (r) => r.totalXp, rank: (r) => r.overallRank },
  { id: 'champions', label: 'Champions', title: 'Champions', copy: 'Career-best leaderboard achievements on public games.', metric: (r) => r.championXp, rank: (r) => r.championRank },
  { id: 'makers', label: 'Makers', title: 'Makers', copy: 'Public games brought to life by the community.', metric: (r) => r.makerXp, rank: (r) => r.makerRank },
  { id: 'explorers', label: 'Explorers', title: 'Explorers', copy: 'Different public games completed.', metric: (r) => r.explorerXp, rank: (r) => r.explorerRank },
  { id: 'recent', label: 'On a roll', title: 'On a roll', copy: 'XP earned over the last 30 days.', metric: (r) => r.recentXp, rank: (r) => r.recentRank },
];

function activeLabel(entry: PlatformLeaderboardEntry, now: number): string {
  return entry.lastActiveAt ? `Active ${formatTimeAgo(entry.lastActiveAt, now)}` : 'Not active yet';
}

function joinedLabel(entry: PlatformLeaderboardEntry): string {
  return `Joined ${new Date(entry.joinedAt).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`;
}

function Member({ entry, view, now }: { entry: PlatformLeaderboardEntry; view: (typeof VIEWS)[number]; now: number }) {
  const score = view.metric(entry);
  return (
    <div className="leaderboard__member">
      <UserAvatar avatarKey={entry.avatarKey} size={40} />
      <div>
        <Link to={`/u/${entry.handle}`} className="leaderboard__handle">@{entry.handle}</Link>
        <div className="muted leaderboard__meta" title={entry.lastActiveAt ? new Date(entry.lastActiveAt).toLocaleString() : undefined}>
          {activeLabel(entry, now)} · {joinedLabel(entry)}
        </div>
      </div>
      <strong className="leaderboard__score">{score.toLocaleString()} XP</strong>
    </div>
  );
}

export function Leaderboard() {
  const services = useServices();
  const now = useNow(60_000);
  const [viewId, setViewId] = useState<PlatformLeaderboardView>('overall');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [result, setResult] = useState<PlatformLeaderboardPage | null>(null);
  const [error, setError] = useState('');
  const view = useMemo(() => VIEWS.find((candidate) => candidate.id === viewId) ?? VIEWS[0], [viewId]);

  useEffect(() => {
    let alive = true;
    const load = () => services.platformLeaderboard({ view: viewId, query, page, pageSize: PAGE_SIZE })
      .then((next) => { if (alive) { setResult(next); setError(''); } })
      .catch((cause: unknown) => { if (alive) setError(cause instanceof Error ? cause.message : 'Could not load Community Rankings.'); });
    void load();
    const timer = window.setInterval(() => void load(), 60_000);
    return () => { alive = false; window.clearInterval(timer); };
  }, [services, viewId, query, page]);

  const selectView = (next: PlatformLeaderboardView) => { setViewId(next); setPage(0); };
  const pages = Math.max(1, Math.ceil((result?.total ?? 0) / PAGE_SIZE));

  return (
    <div className="container leaderboard" data-testid="community-leaderboard">
      <div className="gallery__hero">
        <h1 className="gallery__title">🏆 Community Rankings</h1>
        <p className="gallery__subtitle">Find your place in the flock. Every point has a story.</p>
      </div>
      <div className="leaderboard__tabs" role="tablist" aria-label="Leaderboard views">
        {VIEWS.map((candidate) => <button key={candidate.id} type="button" role="tab" aria-selected={candidate.id === viewId}
          className={`btn btn--sm${candidate.id === viewId ? ' btn--primary' : ' btn--ghost'}`} onClick={() => selectView(candidate.id)}>{candidate.label}</button>)}
      </div>
      <div className="panel leaderboard__intro"><h2 className="panel__title">{view.title}</h2><p className="muted">{view.copy}</p></div>
      <label className="leaderboard__search">Find a member <input value={query} onChange={(event) => { setQuery(event.target.value); setPage(0); }} placeholder="Search @handle" /></label>
      {error && <div className="error-text">{error}</div>}
      {!result && !error && <div className="empty">Tallying the flock…</div>}
      {result && (
        <>
          {result.entries.length === 0 ? <div className="empty">No claimed members match that search yet.</div> : <div className="panel leaderboard__list">
            {result.entries.map((entry) => <div key={entry.uid} className="leaderboard__row"><span className="leaderboard__rank">#{view.rank(entry)}</span><Member entry={entry} view={view} now={now} /></div>)}
          </div>}
          <div className="leaderboard__pagination">
            <button className="btn btn--sm" disabled={page === 0} onClick={() => setPage((current) => current - 1)}>Previous</button>
            <span className="muted">Page {page + 1} of {pages} · {result.total} members</span>
            <button className="btn btn--sm" disabled={page + 1 >= pages} onClick={() => setPage((current) => current + 1)}>Next</button>
          </div>
        </>
      )}
    </div>
  );
}
